import { Clock } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandPaletteGroup } from './CommandPaletteGroup';
import { useAsyncProviderSearch } from './hooks/useAsyncProviderSearch';
import { useRecentItems } from './hooks/useRecentItems';
import { useSyncProviderSearch } from './hooks/useSyncProviderSearch';
import type {
  AsyncCommandProvider,
  CommandProvider,
  CommandResult,
  PaletteMode,
  SyncCommandProvider,
} from './types';

function SyncGroup({
  provider,
  query,
  isScoped,
  selectedId,
  onSelect,
  onHover,
  onViewAll,
}: {
  provider: SyncCommandProvider;
  query: string;
  isScoped: boolean;
  selectedId: string | null;
  onSelect: (result: CommandResult) => void;
  onHover: (id: string) => void;
  onViewAll: (href: string) => void;
}) {
  const results = useSyncProviderSearch(provider, query, 50);

  return (
    <CommandPaletteGroup
      provider={provider}
      results={results}
      query={query}
      isLoading={false}
      isScoped={isScoped}
      onSelect={onSelect}
      onViewAll={onViewAll}
      selectedId={selectedId}
      onHover={onHover}
    />
  );
}

function AsyncGroup({
  provider,
  query,
  enabled,
  isScoped,
  selectedId,
  onSelect,
  onHover,
  onViewAll,
}: {
  provider: AsyncCommandProvider;
  query: string;
  enabled: boolean;
  isScoped: boolean;
  selectedId: string | null;
  onSelect: (result: CommandResult) => void;
  onHover: (id: string) => void;
  onViewAll: (href: string) => void;
}) {
  const maxResults = provider.maxResults ?? 3;
  const { results, isLoading } = useAsyncProviderSearch(provider, query, {
    enabled,
    maxResults: isScoped ? 20 : maxResults,
    isScoped,
  });

  return (
    <CommandPaletteGroup
      provider={provider}
      results={results}
      query={query}
      isLoading={isLoading}
      isScoped={isScoped}
      onSelect={onSelect}
      onViewAll={onViewAll}
      selectedId={selectedId}
      onHover={onHover}
    />
  );
}

// ── Main results component ──

interface CommandPaletteResultsProps {
  mode: PaletteMode;
  providers: CommandProvider[];
  selectedId: string | null;
  onSelect: (result: CommandResult) => void;
  onHover: (id: string) => void;
  onSetRawInput: (value: string) => void;
  onClose: () => void;
}

// Note: onItemsChanged removed — parent polls DOM directly via rAF

export function CommandPaletteResults({
  mode,
  providers,
  selectedId,
  onSelect,
  onHover,
  onSetRawInput,
  onClose,
}: CommandPaletteResultsProps) {
  const navigate = useNavigate();
  const { recentItems } = useRecentItems();

  const handleViewAll = useCallback(
    (href: string) => {
      navigate(href);
      onClose();
    },
    [navigate, onClose]
  );

  const handleRecentClick = useCallback(
    (href: string) => {
      navigate(href);
      onClose();
    },
    [navigate, onClose]
  );

  return (
    <div className="h-[360px] overflow-y-auto">
      {/* Empty mode: show recents */}
      {mode.type === 'empty' && recentItems.length > 0 && (
        <div className="overflow-hidden p-1">
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Recent</div>
          {recentItems.map((item) => (
            <button
              key={item.id}
              type="button"
              data-item-id={item.id}
              onClick={() => handleRecentClick(item.href)}
              onMouseEnter={() => onHover(item.id)}
              className={`flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-2 text-sm outline-hidden ${
                item.id === selectedId ? 'bg-accent text-accent-foreground' : ''
              }`}
            >
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1 min-w-0 text-left">
                <div className="truncate">{item.label}</div>
                {item.subtitle && (
                  <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Scope picker */}
      {mode.type === 'picking_scope' && (
        <div className="overflow-hidden p-1">
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            Filter by type
          </div>
          {providers
            .filter((p) => p.scopeKeyword)
            .filter((p) => !mode.partial || p.scopeKeyword?.toLowerCase().startsWith(mode.partial))
            .map((p) => {
              const Icon = p.icon;
              const itemId = `scope:${p.id}`;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-item-id={itemId}
                  onClick={() => onSetRawInput(`#${p.scopeKeyword} `)}
                  onMouseEnter={() => onHover(itemId)}
                  className={`flex w-full cursor-default select-none items-center gap-3 rounded-sm px-2 py-2 text-sm outline-hidden ${
                    itemId === selectedId ? 'bg-accent text-accent-foreground' : ''
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="w-20 shrink-0 font-mono text-xs text-primary/70">
                    #{p.scopeKeyword}
                  </span>
                  <span className="text-sm text-foreground">{p.label}</span>
                  <span className="ml-auto text-xs text-muted-foreground/40">Search</span>
                </button>
              );
            })}
        </div>
      )}

      {/* Provider groups */}
      {mode.type !== 'picking_scope' &&
        providers.map((provider) => {
          const query = mode.type === 'empty' ? '' : 'query' in mode ? mode.query : '';

          if (mode.type === 'action' && provider.id !== 'shared:actions') return null;
          if (mode.type === 'empty' && provider.kind !== 'sync') return null;
          if (mode.type === 'scoped' && provider.id !== mode.providerId) return null;

          const isScoped = mode.type === 'scoped';

          if (provider.kind === 'sync') {
            return (
              <SyncGroup
                key={provider.id}
                provider={provider}
                query={query}
                isScoped={isScoped}
                selectedId={selectedId}
                onSelect={onSelect}
                onHover={onHover}
                onViewAll={handleViewAll}
              />
            );
          }

          return (
            <AsyncGroup
              key={provider.id}
              provider={provider}
              query={query}
              enabled={mode.type === 'search' || mode.type === 'scoped'}
              isScoped={isScoped}
              selectedId={selectedId}
              onSelect={onSelect}
              onHover={onHover}
              onViewAll={handleViewAll}
            />
          );
        })}
    </div>
  );
}
